import { useState } from 'react';
import { Num, T, useGT } from 'gt-react';
import { DownloadSimpleIcon } from '@phosphor-icons/react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { downloadTextFile } from '../utils/downloadTextFile';
import type { RulesSummary } from './evaluateUploadRules';
import { IssuesSummary } from './IssuesSummary';

/** The AS400 mass load expects this exact file name. */
const UPLOAD_FILE_NAME = 'UPLOAD.csv';

interface Props {
  csv: string;
  summary: RulesSummary;
  /** False until the rules have loaded: never download unchecked. */
  rulesReady: boolean;
}

/**
 * Download CSV, gated by the validation rules: disabled while any line fails a
 * Block rule, and behind a confirmation that repeats the summary when lines
 * fail Warn rules.
 */
export function DownloadCsvButton({ csv, summary, rulesReady }: Props) {
  const gt = useGT();
  const [confirming, setConfirming] = useState(false);
  // Every row skipped: an empty file must not pass for a ready upload.
  const empty = csv === '';
  const blocked = !rulesReady || empty || summary.blockingLineCount > 0;

  function download(): void {
    setConfirming(false);
    // Re-checked here: rules can change while the warning dialog is open.
    if (blocked) return;
    downloadTextFile(UPLOAD_FILE_NAME, 'text/csv;charset=utf-8', csv);
  }

  return (
    <div className="flex items-center gap-2">
      <BlockedReason
        empty={empty}
        blockingLineCount={summary.blockingLineCount}
      />
      <Button
        disabled={blocked}
        onClick={() => {
          if (summary.warningLineCount > 0) setConfirming(true);
          else download();
        }}
      >
        <DownloadSimpleIcon className="h-4 w-4" />
        <T context="button that downloads the generated AS400 upload CSV">
          Download CSV
        </T>
      </Button>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="title of the confirmation before downloading an upload CSV with warnings">
                <Num>{summary.warningLineCount}</Num> lines have warnings
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T context="body of the confirmation before downloading an upload CSV with warnings">
                These lines are still in the CSV. Check them before loading it
                into AS400.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <IssuesSummary summary={summary} />
          <AlertDialogFooter>
            <AlertDialogCancel>
              {gt('Cancel', { context: 'dialog cancel' })}
            </AlertDialogCancel>
            <AlertDialogAction onClick={download} disabled={blocked}>
              {gt('Download Anyway', {
                context: 'confirm downloading an upload CSV with warnings',
              })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BlockedReason({
  empty,
  blockingLineCount,
}: {
  empty: boolean;
  blockingLineCount: number;
}) {
  if (empty) {
    return (
      <span className="text-destructive text-sm">
        <T context="why the upload CSV download is disabled: no line was written">
          Nothing to upload: every row was skipped
        </T>
      </span>
    );
  }
  if (blockingLineCount === 0) return null;
  return (
    <span className="text-destructive text-sm">
      <T context="why the upload CSV download is disabled">
        <Num>{blockingLineCount}</Num> lines fail a Block rule
      </T>
    </span>
  );
}
